import { useCallback, useEffect, useState } from 'react';
import { Pill, ResultCount, type PillVariant } from '@jywer/ui';
import { fetchClients } from './api';
import { ClientDrawer } from './Drawer';
import { STATUS_LABEL, type AdminClientRow, type ClientStatus, type UserRow } from './types';

const STATUS_VARIANT: Record<ClientStatus, PillVariant> = {
  active: 'success',
  paused: 'warning',
  churned: 'muted',
};

export function ClientsPage() {
  const [rows, setRows] = useState<AdminClientRow[]>([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [status, setStatus] = useState<ClientStatus[]>(['active']);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchClients({ status, search, page, pageSize });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Kunne ikke hente kunder');
    } finally {
      setLoading(false);
    }
  }, [status, search, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/admin/users');
        if (!res.ok) return;
        const json = (await res.json()) as { users: UserRow[] };
        if (!cancelled) setUsers(json.users);
      } catch {
        // silently — users list is optional (used only in the drawer's dropdown)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleStatus = (s: ClientStatus) => {
    setPage(1);
    setStatus((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="mb-1 text-2xl font-semibold text-primary">Kunder</h1>
        <p className="text-sm text-ink-2">Rediger kundedata — VAT, adresse, ansvarlig, status.</p>
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded border border-line-1 bg-paper-0 px-4 py-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-ink-2">Status</span>
          <div className="flex flex-wrap gap-1">
            {(['active', 'paused', 'churned'] as ClientStatus[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  status.includes(s)
                    ? 'border-primary bg-primary text-white'
                    : 'border-line-2 bg-paper-0 text-ink-1 hover:border-primary'
                }`}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          placeholder="Søg kunde eller CVR…"
          className="w-64 rounded border border-line-2 px-2.5 py-1.5 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <div className="ml-auto">
          <ResultCount shown={rows.length} total={total} singularNoun="kunde" pluralNoun="kunder" />
        </div>
      </div>

      {err ? (
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          {err}
        </div>
      ) : null}

      {loading && rows.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Henter kunder…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Ingen kunder matcher filtrene.
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-line-1 bg-paper-0">
          <table className="w-full text-sm">
            <thead className="border-b border-line-1 bg-paper-1 text-left text-xs uppercase tracking-wide text-ink-2">
              <tr>
                <th className="px-3 py-2 font-medium">Navn</th>
                <th className="px-3 py-2 font-medium">CVR</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Ansvarlig</th>
                <th className="px-3 py-2 text-right font-medium">Projekter</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setOpenId(r.id)}
                  className="cursor-pointer border-b border-line-1 last:border-b-0 hover:bg-paper-1"
                >
                  <td className="px-3 py-2 text-ink-0">{r.name}</td>
                  <td className="px-3 py-2 text-ink-2 tabular-nums">{r.vat_number ?? '—'}</td>
                  <td className="px-3 py-2">
                    <Pill variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Pill>
                  </td>
                  <td className="px-3 py-2 text-ink-2">{r.default_sales_rep_name ?? '—'}</td>
                  <td className="px-3 py-2 text-right text-ink-2 tabular-nums">
                    {r.project_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 ? (
        <div className="flex items-center justify-center gap-2 text-xs text-ink-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-line-2 px-2 py-1 disabled:opacity-40"
          >
            ← Forrige
          </button>
          <span>
            Side {page} / {lastPage}
          </span>
          <button
            type="button"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            className="rounded border border-line-2 px-2 py-1 disabled:opacity-40"
          >
            Næste →
          </button>
        </div>
      ) : null}

      <ClientDrawer
        clientId={openId}
        users={users}
        onClose={() => setOpenId(null)}
        onSaved={() => {
          void load();
        }}
      />
    </div>
  );
}
